import { isArray } from '@ember/array';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, eq, not, or } from 'ember-truth-helpers';
import ConditionalLinkTo from 'garaje/components/conditional-link-to';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import UiCard from 'garaje/components/ui/card';
import MessageBox from 'garaje/components/ui/classic/message-box';
import type FlowModel from 'garaje/models/flow';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type FlowService from 'garaje/services/flow';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { alias } from 'macro-decorators';
import type ServerError from 'utils/server-error';

interface FlowCardSignature {
  Args: {
    /**
     * flow model to render
     */
    flow: FlowModel;
    /**
     * array of enabled flows models
     */
    enabledFlows?: FlowModel[];
  };
  Element: HTMLDivElement;
}

export default class FlowCard extends Component<FlowCardSignature> {
  @service declare abilities: AbilitiesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare flashMessages: FlashMessagesService;
  @service('flow') declare employeeScreeningFlowService: FlowService;

  @alias('employeeScreeningFlowService.activeEmployeeScreeningFlow.name')
  employeeScreeningFlowName: string | null | undefined;

  get canEditLocalFlow(): boolean {
    return this.abilities.can('edit visitor-types');
  }

  get canEditGlobalFlow(): boolean {
    return this.abilities.can('edit global-flows');
  }

  toggleEnabledTask = dropTask(async (flow: FlowModel) => {
    flow.enabled = !flow.enabled;
    try {
      // @ts-expect-error - custom save parameter
      await flow.save({ propagable: true });
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      const serverError = <ServerError>e;

      flow.enabled = !flow.enabled;
      let title = 'Server error. Please try again.';
      let body = '';

      if (serverError.isAdapterError) {
        title = parseErrorForDisplay(e);
      }

      if (isArray(serverError.errors) && serverError.errors.length === 1) {
        const error = serverError.errors[0];
        if (error?.source && error.source.pointer === '/data/attributes/name') {
          title = error.title;
          body = error.detail;
        }
      }

      this.flashMessages.showFlash('error', title, body);
    }
  });

  <template>
    <UiCard class="is-dragging:shadow-lg-tall trans-box-shadow" data-test-flow={{@flow.name}}>
      <div class="flex space-x-4 items-center">
        <div class="flex-auto flex flex-col space-y-2">
          <GlobalFlowIcon class="ml-1" @show={{@flow.isGlobalChild}}>
            <h4 data-test-flow-name>
              {{@flow.name}}
            </h4>
          </GlobalFlowIcon>
          {{! TODO: Do we remove this? }}
          {{#if @flow.employeeCentric}}
            <p class="text-sm text-carbon-50 mb-0">
              {{#if this.employeeScreeningFlowName}}<span
                  data-test-employee-flow-name={{this.employeeScreeningFlowName}}
                >Registration flow: {{this.employeeScreeningFlowName}}</span>{{/if}}
              Added by
              <ConditionalLinkTo
                @condition={{or (can "visit settings for workplace") (can "visit screening for protect")}}
                @route={{if
                  (can "visit settings for workplace")
                  "workplace.settings.employees.registration"
                  "protect.settings.employees.registration"
                }}
              >
                employee registration
              </ConditionalLinkTo>
            </p>
          {{else if @flow.isGlobalChild}}
            <p class="text-sm text-carbon-50 mb-0">
              Global sign-in flow:
              {{! @glint-expect-error relationship is async, but should be loaded }}
              {{@flow.globalFlow.description}}
            </p>

          {{else if @flow.isWalkup}}
            <p class="text-sm text-carbon-50 mb-0 ml" data-test-walkup-description>
              Standard registration process for walk-in visitors configured by your property admin. This sign in flow
              was added by
              <LinkTo @route="manage.location-settings.index.connect">connecting your location</LinkTo>
              to a property via Envoy Connect
            </p>
          {{/if}}
          {{#unless @flow.isWalkup}}
            {{#if @flow.visitWorkflowEnabled}}
              <label class="checkbox self-start">
                <label class="toggle--tiny">
                  <input type="checkbox" checked={{@flow.enabled}} disabled={{true}} data-test-toggle-on-ipad />
                  <span></span>
                </label>
                Show on iPad
                <span data-test-walkup-policy-text>
                  {{svgJar "question-mark-gray-circle" height="16" class="-mb-0.5 ml-1 cursor-pointer"}}
                  <EmberPopover
                    @side="right"
                    @tooltipClass="!bg-white rounded-lg shadow-md !text-carbon-80 text-sm text-shadow-none"
                    @popperContainer="body"
                    @popoverHideDelay={{300}}
                  >
                    Configuration set by an
                    <a
                      href="https://envoy.help/en/articles/13764979-advanced-visitor-approvals#h_633158c5eb"
                      target="_blank"
                      rel="noopener"
                      class="!text-gem-50"
                    >Approval Policy</a>.
                  </EmberPopover>
                </span>
              </label>
            {{else if (not @flow.visitWorkflowEnabled)}}
              <label class="checkbox self-start">
                <label class="toggle--tiny">
                  <input
                    type="checkbox"
                    checked={{@flow.enabled}}
                    {{! @glint-expect-error ember data types problem - should be boolean }}
                    disabled={{or
                      @flow.isSaving
                      (and @flow.enabled (eq @enabledFlows.length 1))
                      (and @flow.isGlobalChild (not this.canEditGlobalFlow))
                      (cannot "edit visitor-type" @flow)
                    }}
                    {{on "click" (fn this.toggleEnabledTask.perform @flow)}}
                    data-test-toggle-on-ipad
                  />
                  <span></span>
                </label>
                Show on iPad
                {{#if (and @flow.isGlobalChild (not this.canEditGlobalFlow))}}
                  <EmberTooltip
                    @side="right"
                    @text="This is a global sign-in flow. If you need to make changes, please contact a global admin"
                  />
                {{/if}}
              </label>
            {{/if}}
          {{/unless}}
        </div>
        {{#if @flow.employeeCentric}}
          <LinkTo
            @route={{if
              (can "visit settings for workplace")
              "workplace.settings.employees.registration"
              "protect.settings.employees.registration"
            }}
            @disabled={{and (cannot "visit settings for workplace") (cannot "visit screening for protect")}}
            class="btn btn--small btn--outlineFill float-right"
            data-test-edit-flow-button
          >
            View
          </LinkTo>
        {{else if (not @flow.isWalkup)}}
          <LinkTo
            @route="visitors.settings.visitor-types.flow"
            @model={{@flow.id}}
            class="btn btn--small btn--outlineFill float-right"
            data-test-edit-flow-button
          >
            {{#if (or @flow.isGlobalChild (not this.canEditLocalFlow))}}
              View
            {{else}}
              Edit
            {{/if}}
          </LinkTo>
        {{/if}}
      </div>

      {{#if (and @flow.isWalkup @flow.propertyInviteApprovalsEnabled)}}
        <MessageBox
          @type="info"
          @header="Walk-in approvals are enabled by your property admin"
          class="mt-2"
          data-test-flow-property-invite-approvals-enabled
        >
          <p class="mb-0">
            All walk-in visitors who check in at the property must be approved by a tenant admin. Learn more about
            <a
              href="https://envoy.help/en/articles/8395175-connect-visitor-walk-in-approvals"
              target="_blank"
              rel="noopener"
              tabindex="-1"
            >property walk-in approvals</a>.
          </p>
        </MessageBox>
      {{/if}}
    </UiCard>
  </template>
}
